import { useEffect, useReducer } from 'react';

import { logger } from '#utils/logging/logger';

type FetchState<T> = {
	isLoading: boolean;
	hasError: boolean;
	data: T | null;
};

type FetchAction<T> = { type: 'fetch_start' } | { type: 'fetch_success'; data: T } | { type: 'fetch_error' } | { type: 'reset' };

export function useFetch<T>(url: string | null, enabled: boolean = true): FetchState<T> {
	const [state, dispatch] = useReducer(
		(state: FetchState<T>, action: FetchAction<T>): FetchState<T> => {
			switch (action.type) {
				case 'fetch_start':
					return { isLoading: true, hasError: false, data: state.data };
				case 'fetch_success':
					return { isLoading: false, hasError: false, data: action.data };
				case 'fetch_error':
					return { isLoading: false, hasError: true, data: null };
				case 'reset':
					return { isLoading: false, hasError: false, data: null };
			}
		},
		{ isLoading: false, hasError: false, data: null }
	);

	useEffect(() => {
		if (url === null) {
			dispatch({ type: 'reset' });
			return;
		}

		if (!enabled) {
			return;
		}

		const abortController = new AbortController();
		dispatch({ type: 'fetch_start' });

		async function fetchData() {
			try {
				const response = await fetch(url!, { signal: abortController.signal });

				if (!response.ok) {
					dispatch({ type: 'fetch_error' });
					return;
				}

				dispatch({ type: 'fetch_success', data: (await response.json()) as T });
			} catch (err: any) {
				if (err.name !== 'AbortError') {
					logger.error(err);
					dispatch({ type: 'fetch_error' });
				}
			}
		}

		fetchData();
		return () => abortController.abort();
	}, [url, enabled]);

	return state;
}
